import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const fundAnalysis = {
  stars: [
    { name: 'PPFAS Flexicap', status: 'Carrying my portfolio', note: 'Outperforming everything except Midcap 150 and Gold', action: 'INCREASE', color: 'wealth-green' },
    { name: 'HDFC Midcap Opportunities', status: 'Solid & Consistent', note: 'No complaints, reliable performer', action: 'HOLD', color: 'tech-blue' },
    { name: 'Axis Small Cap', status: 'Beat its benchmark', note: 'Rare for active small cap funds', action: 'HOLD', color: 'tech-blue' },
    { name: 'UTI Nifty 50', status: 'Inline with Nifty 50', note: 'Doing exactly what it should', action: 'HOLD', color: 'tech-blue' },
  ],
  disappointments: [
    { name: 'Axis Mid Cap', status: '-3.3% vs Midcap 150', note: 'Underperforming benchmark for MY investments', action: 'WATCH', color: 'missed-gold' },
    { name: 'SBI Small Cap', status: '-4.9% vs Smallcap 250', note: 'Not even close. Clear exit signal.', action: 'EXIT', color: 'alert-red' },
  ]
};

const DashboardMockup = () => {
  return (
    <section className="relative py-24 lg:py-32 bg-deep-space-secondary overflow-hidden" data-testid="dashboard-mockup">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="font-outfit text-3xl lg:text-5xl font-bold text-white tracking-tight">
            The Individual Fund Breakdown
          </h2>
          <p className="mt-4 text-xl text-slate-400 font-ibm-plex max-w-3xl">
            Same investment mimicking against index. Here's what I found about each fund in my portfolio.
          </p>
        </motion.div>
        
        {/* Analysis Dashboard */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* The Stars */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.2)]"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-wealth-green/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-wealth-green" />
              </div>
              <h3 className="font-outfit text-xl font-bold text-wealth-green">The Stars</h3>
            </div>
            
            <div className="space-y-4">
              {fundAnalysis.stars.map((fund, index) => (
                <motion.div
                  key={fund.name}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-ibm-plex text-white font-medium">{fund.name}</div>
                      <div className="text-sm text-slate-400 mt-1">{fund.status}</div>
                      <div className="text-xs text-slate-500 mt-2">{fund.note}</div>
                    </div>
                    <div className={`
                      flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold
                      ${fund.action === 'INCREASE' ? 'bg-wealth-green/20 text-wealth-green' : 
                        fund.action === 'HOLD' ? 'bg-tech-blue/20 text-tech-blue' : ''}
                    `}>
                      {fund.action === 'INCREASE' ? <TrendingUp className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      {fund.action}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* The Disappointments */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.2)]"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-alert-red/20 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-alert-red" />
              </div>
              <h3 className="font-outfit text-xl font-bold text-alert-red">The Disappointments</h3>
            </div>
            
            <div className="space-y-4">
              {fundAnalysis.disappointments.map((fund, index) => (
                <motion.div
                  key={fund.name}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={`
                    border rounded-xl p-4
                    ${fund.action === 'EXIT' ? 'bg-alert-red/10 border-alert-red/30' : 'bg-white/5 border-white/10'}
                  `}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-ibm-plex text-white font-medium">{fund.name}</div>
                      <div className={`text-sm mt-1 font-jetbrains ${fund.action === 'EXIT' ? 'text-alert-red' : 'text-missed-gold'}`}>
                        {fund.status}
                      </div>
                      <div className="text-xs text-slate-500 mt-2">{fund.note}</div>
                    </div>
                    <div className={`
                      flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold
                      ${fund.action === 'EXIT' ? 'bg-alert-red/20 text-alert-red' : 'bg-missed-gold/20 text-missed-gold'}
                    `}>
                      {fund.action === 'EXIT' ? <XCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                      {fund.action}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {/* Important Insight */}
            <div className="mt-6 bg-deep-space/50 border border-white/10 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-missed-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-ibm-plex text-sm font-medium">But Here's What's Interesting:</p>
                  <p className="text-slate-400 text-sm mt-2">
                    During 2024-2025 corrections, my "underperforming" active funds held up much better than the indices. 
                    They cost me some upside, but <span className="text-white">saved me from myself during downside</span>.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* What I'm Doing Now */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 bg-gradient-to-r from-tech-blue/10 to-wealth-green/10 border border-white/10 rounded-2xl p-6 lg:p-10"
        >
          <h3 className="font-outfit text-2xl font-bold text-white mb-6">What I'm Doing Now:</h3>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { action: 'Exiting', target: 'SBI Small Cap', reason: 'No mercy for consistent underperformers', color: 'alert-red' },
              { action: 'Increasing', target: 'PPFAS allocation', reason: "It's proven itself repeatedly", color: 'wealth-green' },
              { action: 'Adding', target: 'Gold allocation', reason: 'That ₹35L lesson hurt. Never ignoring Gold again.', color: 'missed-gold' },
              { action: 'Considering', target: 'Index funds for Mid Cap', reason: 'If Axis Mid Cap keeps losing, why pay expense ratio?', color: 'tech-blue' },
            ].map((item, index) => (
              <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className={`font-jetbrains text-sm font-bold ${
                  item.color === 'alert-red' ? 'text-alert-red' :
                  item.color === 'wealth-green' ? 'text-wealth-green' :
                  item.color === 'missed-gold' ? 'text-missed-gold' :
                  'text-tech-blue'
                }`}>
                  {item.action}
                </div>
                <div className="font-ibm-plex text-white font-medium mt-1">{item.target}</div>
                <div className="text-xs text-slate-400 mt-2">{item.reason}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DashboardMockup;
